import { useState, useEffect, useCallback } from 'react';
import { http } from '../lib/http';

export interface UserPermissions {
  role: string;
  permissions: {
    admin: boolean;
    dispatcher: boolean;
    command: boolean;
    'shift-commander': boolean;
    unit: boolean;
  };
}

export const usePermissions = () => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await http.get('/auth/user-permissions');
      
      if (response.data.success) {
        setPermissions(response.data.data);
      } else {
        setError('Failed to fetch permissions');
        setPermissions(null);
      }
    } catch (err: any) {
      console.error('Error fetching permissions:', err);
      const errorMsg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : 'Failed to fetch permissions';
      setError(errorMsg);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPermission = useCallback(async (permission: string): Promise<boolean> => {
    try {
      const response = await http.get(`/auth/check-permission/${permission}`);
      
      if (response.data.success) {
        return response.data.data.hasPermission;
      }
      
      return false;
    } catch (err: any) {
      console.error(`Error checking permission ${permission}:`, err);
      return false;
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    refetch: fetchPermissions,
    checkPermission,
    hasPermission: (permission: keyof UserPermissions['permissions']) => {
      return permissions?.permissions[permission] || false;
    }
  };
};

